import React, { useEffect, useState, useRef, memo } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useQueryClient } from "@tanstack/react-query";
import { ChatMessage as ChatMessageType } from "@/types/chat";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { User, AlertCircle, History, Eye, Play, Pause, Volume2, FileText, Download, Check, Clock, XCircle, Megaphone, XOctagon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MediaViewer } from "@/components/MediaViewer";
import { supabase, chatSupabase } from "@/integrations/supabase/client";
import { normalizePhone } from "@/utils/phoneUtils";
import { getDefaultAvatar, getDefaultOperatorAvatar } from "@/utils/avatarUtils";

// Cache de profiles compartilhado entre todos os ChatMessage da sessão (evita N+1 por mensagem de operador)
const _profilesCache = new Map<string, { name: string | null; avatar_url: string | null }>();
// Promises em andamento: evita que N mensagens com o mesmo sender disparem N requisições simultâneas
const _profilesPending = new Map<string, Promise<{ name: string | null; avatar_url: string | null }>>();

function _fetchProfileCached(
  key: string,
  fetcher: () => Promise<{ name: string | null; avatar_url: string | null }>
): Promise<{ name: string | null; avatar_url: string | null }> {
  if (_profilesCache.has(key)) return Promise.resolve(_profilesCache.get(key)!);
  if (!_profilesPending.has(key)) {
    const p = fetcher().then(entry => {
      _profilesCache.set(key, entry);
      _profilesPending.delete(key);
      return entry;
    }).catch(() => {
      _profilesPending.delete(key);
      return { name: null, avatar_url: null };
    });
    _profilesPending.set(key, p);
  }
  return _profilesPending.get(key)!;
}

// URLs que não devem ser carregadas diretamente no front — definidas em módulo-level para reutilização em effects
const _isBlockedMediaUrl = (url: string | null | undefined): boolean =>
  !!url && (
    url.includes('mmg.whatsapp.net') ||   // CDN WhatsApp: CORS/cache/QUIC falham
    url.includes('.enc') ||                // arquivos .enc não são imagens diretas
    url.includes('api.fluity.com.br/v1/media') // URLs legadas que requerem auth de servidor
  );

const _isInvalidMediaUrl = (url: string | null | undefined): boolean =>
  !url || typeof url !== 'string' || url === 'false' || url === 'true' || !/^(https?|data:|blob:)/i.test(url.trim());

interface ChatMessageProps {
  message: ChatMessageType;
  instanceId?: string | null;
  phone?: string | null;
}

// Componente de player de áudio via URL
const AudioPlayer = ({ audioId, audioUrl, fallbackUrl, fileName }: {
  audioId: string;
  audioUrl: string;
  fallbackUrl?: string | null;
  fileName?: string | null;
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [triedFallback, setTriedFallback] = useState(false);
  const [permissionError, setPermissionError] = useState(false);

  const effectiveUrl = (audioUrl || '').trim();

  const formatAudioTime = (seconds: number) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    setLoadError(false);
    setTriedFallback(false);
    setPermissionError(false);
  }, [effectiveUrl, fallbackUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const srcToLoad = (triedFallback && fallbackUrl) ? fallbackUrl : effectiveUrl;
    if (!srcToLoad) return;

    const handleTimeUpdate = () => {
      if (audio) {
        setCurrentTime(audio.currentTime);
        setIsPlaying(!audio.paused);
      }
    };

    const handleLoadedMetadata = () => {
      if (audio) {
        const d = audio.duration;
        if (Number.isFinite(d) && d >= 0) setDuration(d);
        setLoadError(false);
      }
    };
    const handleDurationChange = () => {
      if (audio && Number.isFinite(audio.duration) && audio.duration >= 0) setDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleError = () => {
      if (fallbackUrl && !triedFallback && srcToLoad === effectiveUrl) {
        setTriedFallback(true);
        setLoadError(false);
        audio.src = fallbackUrl.trim();
        audio.load();
      } else {
        setLoadError(true);
        setIsPlaying(false);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    audio.src = srcToLoad.trim();
    audio.load();

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.pause();
      audio.src = '';
    };
  }, [effectiveUrl, fallbackUrl, triedFallback]);

  const handlePlayPause = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (loadError) return;
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.muted = false;
      audio.volume = 1;
      audio.play().catch((err: unknown) => {
        const isNotAllowed = err instanceof DOMException && (err.name === 'NotAllowedError' || err.code === 20);
        if (isNotAllowed) {
          setPermissionError(true);
        } else {
          setLoadError(true);
        }
      });
    }
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const urlToUse = effectiveUrl || audioUrl;
    if (!urlToUse) return;
    window.open(urlToUse, '_blank');
  };
  
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };
  
  if (permissionError) {
    return (
      <div className="w-full max-w-[320px] bg-background/80 backdrop-blur-sm rounded-2xl p-4 border border-border/50 shadow-sm" data-audio-player>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <Volume2 className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0 space-y-1">
            <p className="text-[11px] font-medium text-muted-foreground truncate italic">
              {fileName || 'Mensagem de voz'}
            </p>
            <p className="text-xs text-muted-foreground">
              Permita o áudio no navegador: clique no ícone de som/cadeado na barra de endereço do Chrome e permita &quot;Som&quot; para este site.
            </p>
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" onClick={(e) => { setPermissionError(false); handlePlayPause(e); }}>
                Tentar reproduzir novamente
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownload}>
                Baixar arquivo
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="w-full max-w-[320px] bg-background/80 backdrop-blur-sm rounded-2xl p-4 border border-border/50 shadow-sm" data-audio-player>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
            <Volume2 className="h-6 w-6 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-medium text-muted-foreground truncate italic">
              {fileName || 'Mensagem de voz'}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Reprodução indisponível para este áudio.
            </p>
            <Button variant="outline" size="sm" className="mt-2" onClick={handleDownload}>
              Baixar arquivo
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const waveformBars = [35,55,75,50,80,65,45,70,90,60,75,50,85,65,55,75,90,60,70,45,80,60,50,65];
  const progress = duration > 0 ? currentTime / duration : 0;

  return (
    <div className="flex items-center gap-3 py-1.5 px-1 min-w-[220px] max-w-[280px]" data-audio-player>
      <audio ref={audioRef} preload="metadata" className="hidden" />
      <button
        type="button"
        onClick={handlePlayPause}
        className="h-10 w-10 rounded-full bg-[#002172] flex items-center justify-center flex-shrink-0 shadow-md hover:opacity-90 transition-opacity"
      >
        {isPlaying ? (
          <Pause className="h-4 w-4 text-white" fill="white" />
        ) : (
          <Play className="h-4 w-4 text-white ml-0.5" fill="white" />
        )}
      </button>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="relative flex items-end gap-[2px] h-7">
          {waveformBars.map((h, i) => (
            <div
              key={i}
              className={`flex-1 min-w-[2px] max-w-[4px] rounded-sm transition-colors duration-100 ${i / waveformBars.length < progress ? 'bg-[#002172]' : 'bg-[#c5d0dc]'}`}
              style={{ height: `${h}%` }}
            />
          ))}
          <input
            type="range"
            min="0"
            max={duration || 0}
            step="0.01"
            value={currentTime}
            onChange={handleSeek}
            className="absolute inset-0 w-full opacity-0 cursor-pointer"
          />
        </div>
        <span className="text-[10px] font-mono text-[#94a3b8] tabular-nums">
          {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
        </span>
      </div>
    </div>
  );
};

export const ChatMessage = memo(function ChatMessage({ message, instanceId, phone }: ChatMessageProps) {
  const { user } = useAuth();
  const { currentOrg } = useOrganizationContext();
  const queryClient = useQueryClient();
  const [contactName, setContactName] = useState<string | null>(
    (message as any).contactName ?? (message as any).nomewpp ?? null
  );
  const [contactPictureB64, setContactPictureB64] = useState<string | null>(null);
  const [platformUserName, setPlatformUserName] = useState<string | null>(null);
  const [platformUserAvatarUrl, setPlatformUserAvatarUrl] = useState<string | null>(null);
  const [showMediaViewer, setShowMediaViewer] = useState(false);
  const [mediaInfo, setMediaInfo] = useState<{
    fileType: 'image' | 'video' | 'audio' | 'document';
    fileName?: string;
  } | null>(null);
  const [lazyMediaUrl, setLazyMediaUrl] = useState<string | null>(null);
  const [lazyMediaType, setLazyMediaType] = useState<string | null>(null);
  // Atrasa o carregamento de mídia para que o texto da mensagem renderize primeiro.
  const [mediaReady, setMediaReady] = useState(false);
  // Rastreia URLs de imagem que falharam para tentar o próximo fallback
  const [userImgBrokenSrcs, setUserImgBrokenSrcs] = useState<Set<string>>(new Set());
  const [botImgBrokenSrcs, setBotImgBrokenSrcs] = useState<Set<string>>(new Set());

  const lazyMediaFetchedIdRef = useRef<string | null>(null);
  const operatorProfileFetchedRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setMediaReady(true), 300);
    return () => clearTimeout(t);
  }, []);

  const isWhatsAppCdnOrEnc = _isBlockedMediaUrl;
  const isInvalidMediaUrl = _isInvalidMediaUrl;

  // Lazy load de media_url: busca do banco quando a mensagem tem mídia mas sem URL pré-carregada
  useEffect(() => {
    if (!mediaReady) return; // aguarda texto renderizar antes de buscar mídia do banco
    const msgMediaUrl = (message as any).media_url;
    const msgId = (message as any).message_id ?? (message as any).id;
    const msgMediaType = (message as any).media_type;
    const msgMessageType = (message as any).message_type ?? "";
    const isMediaMsg = !!msgMediaType || ['image','audio','video','file','document','media','audiomessage','imagemessage','videomessage','documentmessage','ptt'].includes(String(msgMessageType).toLowerCase());
    if (!msgId || !isMediaMsg) return;
    if (msgMediaUrl && !_isBlockedMediaUrl(msgMediaUrl) && !_isInvalidMediaUrl(msgMediaUrl)) return;
    if (lazyMediaFetchedIdRef.current === String(msgId)) return;
    lazyMediaFetchedIdRef.current = String(msgId);
    chatSupabase
      .from('messages')
      .select("media_url, media_type")
      .eq("message_id", msgId)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.media_url && !_isInvalidMediaUrl(data.media_url) && !_isBlockedMediaUrl(data.media_url)) setLazyMediaUrl(data.media_url);
        if (data?.media_type) setLazyMediaType(data.media_type);
      });
  }, [(message as any).message_id, (message as any).id, (message as any).media_url, (message as any).media_type, (message as any).message_type, mediaReady]);

  const formatTime = (dateString: string) => {
    if (!dateString) return "";
    return format(new Date(dateString), "HH:mm", { locale: ptBR });
  };

  // Tabela unificada (chat_messages_instagram) usa content_message; legado usa content / user_message / bot_message
  const messageContent = (message as any).content ?? (message as any).content_message ?? message.user_message ?? message.bot_message ?? "";
  const senderType = (message as any).sender_type;
  
  // Verificar se tem mídia (base64, URL ou media_type) — inclui dados lazy-loaded do banco
  const rawMessageType = (message.message_type ?? "").toString().toLowerCase();

  const rawMediaUrl = (message as any).media_url ?? lazyMediaUrl;
  const hasMediaUrl = !!rawMediaUrl && !isInvalidMediaUrl(rawMediaUrl) && !isWhatsAppCdnOrEnc(rawMediaUrl);
  const mediaUrl = hasMediaUrl ? rawMediaUrl : null;

  const hasMediaType = !!(message as any).media_type || !!lazyMediaType;
  const rawMediaType = (((message as any).media_type ?? lazyMediaType) ?? "").toString().toLowerCase();
  // message_type pode ser 'image', 'audio', 'video', 'file', 'document', 'media' (Instagram) ou variantes Evolution/WhatsApp
  const messageTypeIsMedia = message.message_type &&
    ['image', 'audio', 'video', 'file', 'document', 'media', 'audiomessage', 'imagemessage', 'videomessage', 'documentmessage', 'ptt'].includes(rawMessageType);
  const hasMedia = hasMediaUrl || hasMediaType || messageTypeIsMedia;
  const mediaFileName = (message as any).media_file_name;

  // Inferir categoria de mídia a partir de message_type quando media_type for genérico (ex: application/octet-stream)
  const inferredMediaCategory =
    rawMessageType.includes('audio') || rawMessageType === 'ptt' ? 'audio'
      : rawMessageType.includes('image') ? 'image'
        : rawMessageType.includes('video') ? 'video'
          : rawMessageType.includes('document') ? 'document'
            : null;
  const mediaTypeRaw = (message as any).media_type ?? lazyMediaType ?? message.message_type;
  const mediaType = inferredMediaCategory || mediaTypeRaw;
  
  // Instagram: user_sender = origem do contato ou "Cliente" = mensagem do cliente (esquerda). Operador/Bot/Sistema ou nome do operador = direita.
  const userSender = (message as any).user_sender;
  const agentId = (message as any).agent_id as string | null | undefined;
  const userSenderStr = String(userSender ?? '').trim();
  const contentOrContentMessage = (message as any).content ?? (message as any).content_message;
  const hasContent = contentOrContentMessage && String(contentOrContentMessage).trim() !== "";
  const isUuidStr = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
  // agent_id preenchido = mensagem enviada por usuário da plataforma (operador/bot), sempre lado direito.
  const hasAgentId = !!agentId && isUuidStr(String(agentId));
  // user_sender com espaço = nome de pessoa (operador); UUID = usuário da plataforma; agentId preenchido = operador.
  const isOperatorOrBotBySender = hasAgentId || ['Operador', 'Bot', 'Sistema'].includes(userSenderStr) || (userSenderStr !== '' && userSenderStr !== 'Cliente' && /\s/.test(userSenderStr)) || (userSenderStr !== '' && isUuidStr(userSenderStr));
  const isUserMessageType = message.message_type === 'user' || message.message_type === 'messages.upsert';
  const isOperatorOrBotByType = senderType === 'operator' || senderType === 'bot' || senderType === 'system';
  const isFromClientBySender = userSender !== undefined && userSender !== null && !isOperatorOrBotBySender;
  // Contato (esquerda) só se sender_type for contact/user ou user_sender indicar cliente (não operador/bot).
  const isContactSender = senderType === 'contact' || senderType === 'user' || (!isOperatorOrBotByType && isFromClientBySender);
  const hasUserMessage = (message.user_message && message.user_message.trim() !== "") ||
                         (hasContent && (isUserMessageType || isContactSender));

  // Operador/bot/sistema (lado direito): sender_type ou user_sender (literal ou nome com espaço)
  const isBotSender = isOperatorOrBotByType || isOperatorOrBotBySender;
  const isBotMessageType = message.message_type === 'bot';

  const hasUserMedia = (hasMedia && (isContactSender || (!senderType && !message.bot_message && !contentOrContentMessage && !isBotSender))) ||
                       (messageTypeIsMedia && !message.bot_message && !(contentOrContentMessage as string) && !isBotSender);

  // É mensagem do usuário (Cliente): esquerda, com nome do cliente
  const isUserMessage = !isBotMessageType && (
    (isUserMessageType && (hasUserMessage || hasUserMedia)) ||
    (isContactSender && (hasUserMessage || hasUserMedia || hasContent)) ||
    (hasMedia && !message.bot_message && !isBotSender) ||
    (messageTypeIsMedia && !message.bot_message && !isBotSender)
  );
  // É mensagem do bot/operador/sistema (lado direito)
  const hasBotMessage = (message.bot_message && message.bot_message.trim() !== "") ||
                        isBotMessageType ||
                        (isBotSender && (hasContent || hasMedia || messageTypeIsMedia)) ||
                        (hasContent && !isUserMessage && (isBotSender || (!senderType && !hasMedia && !messageTypeIsMedia)));
  
  const detectMediaFile = (messageText: string) => {
    // Aceita "imagem: nome" com ou sem prefixo 📎 (webhook/legado pode salvar sem emoji)
    const mediaPattern = /(?:📎\s*)?(imagem|vídeo|áudio|documento):\s*(.+)/i;
    const match = messageText.match(mediaPattern);
    
    if (match) {
      const [, type, fileName] = match;
      const fileTypeMap: Record<string, 'image' | 'video' | 'audio' | 'document'> = {
        'imagem': 'image',
        'vídeo': 'video',
        'áudio': 'audio',
        'documento': 'document'
      };
      return { fileType: fileTypeMap[type.toLowerCase()] || 'document', fileName: fileName.trim() };
    }
    
    // Usar a variável hasMedia já definida acima
    if (hasMedia) {
      return {
        fileType: mediaType as 'image' | 'video' | 'audio' | 'document',
        fileName: mediaFileName || 'arquivo'
      };
    }
    return null;
  };

  const handleDownloadFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = mediaUrl ?? mediaFallbackUrl;
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = mediaFileName || 'arquivo';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  };

  // Fonte de mídia: media_url (Supabase Storage)
  const mediaDataUrl = mediaUrl ?? null;

  // Determina se a mídia pode ser renderizada (tem URL e tipo)
  const canRenderMedia = !!(hasMedia && mediaType && mediaDataUrl);

  // Encontra o primeiro src de imagem que ainda não foi marcado como quebrado
  const userEffectiveImageSrc = mediaUrl && !userImgBrokenSrcs.has(mediaUrl) ? mediaUrl : null;
  const botEffectiveImageSrc = mediaUrl && !botImgBrokenSrcs.has(mediaUrl) ? mediaUrl : null;

  // Vídeo e áudio: apenas via URL
  const videoPlayableSrc = mediaUrl ?? undefined;
  const audioPrimaryUrl = mediaUrl ?? '';
  const audioFallbackUrl: string | null = null;


  const getAttendantName = (botMessage: string) => {
    const match = botMessage.match(/^\[([^\]]+)\]/);
    return match ? match[1] : 'Atendente';
  };

  const getMessageContent = (botMessage: string) => {
    return botMessage.replace(/^\[([^\]]+)\]\n?/, '');
  };

  const handleViewMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    const content = getMessageContent(message.bot_message || (message as any).content_message || (message as any).content || '');
    const media = detectMediaFile(content);
    if (media) {
      setMediaInfo(media);
      setShowMediaViewer(true);
    }
  };

  const botMessageText = message.bot_message || (message as any).content || (message as any).content_message || "";
  const isClosureMessage = hasBotMessage && /^Chat encerrado/i.test(botMessageText.trim());
  const isSystemMessage = hasBotMessage && (
    isClosureMessage ||
    botMessageText.includes('foi atribuído para') ||
    botMessageText.includes('foi desatribuído de') ||
    botMessageText.includes('encerrado por') ||
    botMessageText.includes('Tag "')
  );

  // WhatsApp: identificado pelo texto "📝 NOTA INTERNA:" no bot_message. Instagram: identificado pela coluna is_internal na tabela chat_messages_instagram.
  const isInternalNote = (message as any).is_internal === true || (hasBotMessage && botMessageText.includes('📝 NOTA INTERNA:'));
  const isDisparoMessage = (message as any).message_type === 'disparo';

  const isDeleted = (message as any).is_deleted === true;
  const isEdited = !!(message as any).edited_at;
  const originalMessage = (message as any).original_message;
  const editHistory = (message as any).edit_history as any[] || [];

  const isUuid = (v: string | null | undefined) =>
    typeof v === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v.trim());

  // Buscar nome e foto do usuário da plataforma: por UUID (user_sender ou agent_id) ou por nome
  // Cache de módulo (_profilesCache) evita N+1: cada UUID/nome só vai ao banco uma vez por sessão.
  useEffect(() => {
    if (!hasBotMessage) return;
    if (operatorProfileFetchedRef.current) return;
    operatorProfileFetchedRef.current = true;

    const applyProfile = (p: { name: string | null; avatar_url: string | null }) => {
      if (p.name) setPlatformUserName(p.name);
      if (p.avatar_url) setPlatformUserAvatarUrl(p.avatar_url);
    };

    const fetchProfile = async () => {
      // Prioridade 1: user_sender é UUID
      if (isUuid(userSender)) {
        const key = `id:${String(userSender).trim()}`;
        const entry = await _fetchProfileCached(key, async () => {
          const { data } = await supabase.from("profiles").select("name, avatar_url").eq("id", String(userSender).trim()).maybeSingle();
          return { name: data?.name ?? null, avatar_url: data?.avatar_url ?? null };
        });
        applyProfile(entry);
        return;
      }
      // Prioridade 2: agent_id é UUID
      if (isUuid(agentId)) {
        const key = `id:${String(agentId).trim()}`;
        const entry = await _fetchProfileCached(key, async () => {
          const { data } = await supabase.from("profiles").select("name, avatar_url").eq("id", String(agentId).trim()).maybeSingle();
          return { name: data?.name ?? null, avatar_url: data?.avatar_url ?? null };
        });
        applyProfile(entry);
        return;
      }
      // Prioridade 3: user_sender é nome literal do operador
      const senderStr = userSenderStr;
      if (['Operador', 'Bot', 'Sistema', ''].includes(senderStr) || senderStr.length < 2) return;
      const key = `name:${senderStr.toLowerCase()}`;
      const entry = await _fetchProfileCached(key, async () => {
        const { data: rows } = await supabase.from("profiles").select("id, name, avatar_url").ilike("name", senderStr).limit(1);
        const data = rows?.[0];
        return { name: data?.name ?? null, avatar_url: data?.avatar_url ?? null };
      });
      applyProfile(entry);
    };
    fetchProfile();
  }, [hasBotMessage, userSender, userSenderStr, agentId]);

  // Rótulo do remetente (lado direito): Bot, Aplicativo, Operador ou nome do usuário (user_sender ou UUID)
  const isKnownLiteral = ['Operador', 'Bot', 'Sistema'].includes(userSenderStr);
  const isRealName = userSenderStr !== '' && !isKnownLiteral && !isUuid(userSenderStr);
  const resolvedAttendantName =
    userSender === "Sistema"
      ? "Aplicativo"
      : userSender === "Operador"
        ? "Operador"
        : isUuid(userSender)
          ? (platformUserName ?? "Usuário")
          : userSender === "Bot"
            ? (platformUserName ?? "Bot")
            : hasAgentId
              ? (platformUserName ?? "Usuário")
              : isRealName || (senderType === "operator" && userSender)
                ? userSenderStr || (userSender as string)
                : hasBotMessage
                  ? getAttendantName(botMessageText)
                  : null;
  const attendantName = resolvedAttendantName;
  const isAttendantMessage = hasBotMessage && !isSystemMessage && !isInternalNote && attendantName && attendantName !== 'Atendente';
  // Avatar do operador: foto do perfil da plataforma (por UUID ou por nome), senão imagem padrão
  const defaultOperatorAvatar = getDefaultOperatorAvatar();
  const [brokenOperatorAvatarSrc, setBrokenOperatorAvatarSrc] = useState<string | null>(null);
  const operatorImgSrc =
    platformUserAvatarUrl && platformUserAvatarUrl !== brokenOperatorAvatarSrc
      ? platformUserAvatarUrl
      : defaultOperatorAvatar;

  // Buscar nome do contato (tabela contacts): por phone/whatsapp ou Account_ID_insta
  useEffect(() => {
    const phoneOrOrigem = message.phone || (message as any).origem_contato;
    if (!isUserMessage || !message.instance_id || !currentOrg?.id || !phoneOrOrigem || contactName) return;

    const fetchContactName = async () => {
      try {
        const msgPhone = message.phone ?? '';
        const msgOrigem = (message as any).origem_contato ?? '';
        const normMsgPhone = normalizePhone(msgPhone || msgOrigem);

        // Preferir cache já populado pelo useInstagramChats (contacts-unified, staleTime 120s)
        const cachedContacts = queryClient.getQueryData<any[]>(["contacts-unified", currentOrg.id]) ?? [];

        let contacts: any[] = cachedContacts;

        if (!contacts.length) {
          // Cache vazio: busca direcionada (não full-scan)
          const orFilter = msgOrigem
            ? `phone.eq.${msgPhone},whatsapp.eq.${msgPhone},Account_ID_insta.eq.${msgOrigem},phone.eq.${normMsgPhone},whatsapp.eq.${normMsgPhone}`
            : `phone.eq.${msgPhone},whatsapp.eq.${msgPhone},phone.eq.${normMsgPhone},whatsapp.eq.${normMsgPhone}`;
          const { data } = await supabase
            .from('contacts')
            .select('phone, name, whatsapp, "Account_ID_insta", picture_b64')
            .eq('org_id', currentOrg.id)
            .or(orFilter)
            .limit(5);
          contacts = data ?? [];
        }

        if (!contacts.length) {
          setContactName(phoneOrOrigem);
          setContactPictureB64(null);
          return;
        }

        const contact = contacts.find((c: any) => {
          const cPhone = normalizePhone(c.phone || '');
          const cWhatsapp = normalizePhone(c.whatsapp || '');
          const cAccountInsta = (c.Account_ID_insta ?? '').toString().trim();
          return (
            (normMsgPhone && (cPhone === normMsgPhone || cWhatsapp === normMsgPhone)) ||
            (msgOrigem && cAccountInsta && (cAccountInsta === msgOrigem || cAccountInsta === msgPhone)) ||
            (msgPhone && cAccountInsta && cAccountInsta === msgPhone)
          );
        });

        if (contact) {
          const displayName = (contact.name ?? '').trim();
          setContactName(displayName || contact.phone || (contact as any).whatsapp || phoneOrOrigem);
          const rawPic = (contact as any).picture_b64;
          setContactPictureB64((rawPic && typeof rawPic === 'string' && !rawPic.startsWith('{')) ? rawPic : null);
        } else {
          setContactName(phoneOrOrigem);
          setContactPictureB64(null);
        }
      } catch (error) {
        console.error('Erro ao buscar nome do contato:', error);
        setContactName(phoneOrOrigem);
        setContactPictureB64(null);
      }
    };

    fetchContactName();
  }, [isUserMessage, message.phone, (message as any).origem_contato, message.instance_id, currentOrg?.id, contactName]);

  const userDefaultAvatar = getDefaultAvatar(message.phone || (message as any).origem_contato || message.id || "");
  const [brokenProfileSrc, setBrokenProfileSrc] = useState<string | null>(null);
  const contactPictureSrc = contactPictureB64?.startsWith("http") ? contactPictureB64 : null;
  const userImgSrc = (contactPictureSrc && contactPictureSrc !== brokenProfileSrc)
    ? contactPictureSrc
    : userDefaultAvatar;

  return (
    <div className="space-y-4">
      {isUserMessage && (
        <div className="flex justify-start">
          {!contactName ? (
            /* Skeleton enquanto o nome do contato está carregando */
            <div className="flex gap-3 max-w-[70%]">
              <div className="h-8 w-8 rounded-full bg-[#002172]/20 animate-pulse shrink-0" />
              <div className="space-y-1.5">
                <div className="h-3 w-20 rounded bg-[#002172]/20 animate-pulse" />
                <div className="h-10 w-48 rounded-bl-[16px] rounded-br-[16px] rounded-tl-[4px] rounded-tr-[16px] bg-[#002172]/20 animate-pulse" />
              </div>
            </div>
          ) : (
            <div className="flex gap-3 max-w-[70%] min-w-0 animate-in fade-in slide-in-from-bottom-1 duration-300">
            <Avatar className="h-8 w-8 flex-shrink-0" data-message-avatar>
              <AvatarImage
                src={userImgSrc}
                className="object-cover"
                onError={() => {
                  if (userImgSrc && userImgSrc !== userDefaultAvatar) setBrokenProfileSrc(userImgSrc);
                }}
              />
              <AvatarFallback className="bg-[#002172] text-white">
                <User className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
            <div className="bg-white rounded-bl-[16px] rounded-br-[16px] rounded-tl-[4px] rounded-tr-[16px] px-[16px] pt-[12px] pb-[12px] shadow-[0px_2px_8px_0px_rgba(0,0,0,0.08)] min-w-0 overflow-hidden" data-user-message>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-medium text-[#64748b]">{contactName}</span>
                <span className="text-[10px] text-[#94a3b8]" data-message-timestamp>{formatTime(message.created_at || "")}</span>
              </div>
              <div className="space-y-2">
                {(message.user_message || (message as any).content || (message as any).content_message) && (
                  <p className="text-sm text-[#111827] break-words overflow-wrap-anywhere">{message.user_message || (message as any).content || (message as any).content_message || ''}</p>
                )}
                {/* Se não tem texto mas mídia não pode ser renderizada, mostrar indicador */}
                {!message.user_message && !(message as any).content && !(message as any).content_message && hasMedia && !canRenderMedia && (
                  <p className="text-sm text-[#64748b] italic">
                    {mediaType?.includes('image') ? '📷 Imagem' :
                     mediaType?.includes('video') ? '🎥 Vídeo' :
                     mediaType?.includes('audio') ? '🎵 Áudio' :
                     '📎 Arquivo'}
                  </p>
                )}
                {canRenderMedia && mediaType && (
                  <div className="mt-2">
                    {mediaType.includes('image') && userEffectiveImageSrc && (
                      <div className="relative group max-w-[240px] overflow-hidden rounded-xl shadow-md cursor-pointer" data-chat-media onClick={() => { setMediaInfo({fileType: 'image', fileName: mediaFileName || 'imagem.jpg'}); setShowMediaViewer(true); }}>
                        <img
                          src={userEffectiveImageSrc}
                          className="w-full h-auto max-h-[300px] object-cover"
                          alt={mediaFileName || 'Imagem'}
                          loading="lazy"
                          onError={() => setUserImgBrokenSrcs(prev => new Set([...prev, userEffectiveImageSrc]))}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />
                      </div>
                    )}
                    {mediaType.includes('image') && !userEffectiveImageSrc && (
                      <p className="text-sm text-[#64748b] italic">📷 Imagem indisponível</p>
                    )}
                    {mediaType.includes('audio') && audioPrimaryUrl && (
                      <div className="max-w-[240px]">
                        <AudioPlayer
                          audioId={`${message.id}-audio`}
                          audioUrl={audioPrimaryUrl}
                          fallbackUrl={audioFallbackUrl && audioFallbackUrl !== audioPrimaryUrl ? audioFallbackUrl : null}
                          fileName={mediaFileName}
                        />
                      </div>
                    )}
                    {mediaType.includes('video') && videoPlayableSrc && (
                      <div className="max-w-[240px] rounded-lg overflow-hidden border border-border/50" data-chat-media>
                        <video
                          src={videoPlayableSrc}
                          className="w-full h-auto max-h-[300px]"
                          controls
                          preload="metadata"
                          playsInline
                        />
                      </div>
                    )}
                    {mediaType.includes('document') && (
                      <div className="flex items-center gap-2.5 max-w-[260px] bg-black/5 rounded-xl p-2.5">
                        <div className="h-9 w-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                          <FileText className="h-5 w-5 text-red-500" />
                        </div>
                        <div
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => { setMediaInfo({fileType: 'document', fileName: mediaFileName || 'arquivo.pdf'}); setShowMediaViewer(true); }}
                        >
                          <p className="text-[13px] font-medium text-[#111827] truncate leading-tight">{mediaFileName || 'Documento'}</p>
                          <p className="text-[11px] text-[#64748b]">Toque para visualizar</p>
                        </div>
                        <button
                          className="h-8 w-8 rounded-lg bg-white/80 flex items-center justify-center text-[#64748b] hover:text-[#002172] transition-colors flex-shrink-0 shadow-sm"
                          onClick={handleDownloadFile}
                          title="Baixar arquivo"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {(() => {
                  const msgReactions = (message as any).reactions;
                  if (!Array.isArray(msgReactions) || msgReactions.length === 0) return null;
                  const grouped: Record<string, number> = {};
                  for (const r of msgReactions) {
                    if (r?.emoji) grouped[r.emoji] = (grouped[r.emoji] || 0) + 1;
                  }
                  const entries = Object.entries(grouped);
                  if (entries.length === 0) return null;
                  return (
                    <div className="flex flex-wrap gap-1 pt-1 border-t border-gray-100 mt-1">
                      {entries.map(([emoji, count]) => (
                        <span key={emoji} className="inline-flex items-center gap-0.5 bg-gray-100 rounded-full px-1.5 py-0.5 text-sm leading-none text-gray-700">
                          {emoji}
                          {count > 1 && <span className="text-[10px]">{count}</span>}
                        </span>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      {hasBotMessage && (
        <div className={`flex ${isSystemMessage ? 'justify-center' : 'justify-end'}`}>
          {isClosureMessage ? (
            <div className="w-full flex flex-col items-center gap-2 py-1" data-system-message>
              <div className="flex items-center gap-3 w-full">
                <div className="flex-1 h-px bg-border" />
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full border bg-background text-[11px] font-medium text-muted-foreground shrink-0">
                  <XCircle className="h-3.5 w-3.5 text-rose-400" />
                  <span>Conversa encerrada</span>
                </div>
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="w-full rounded-2xl border bg-muted/20 overflow-hidden">
                {(() => {
                  const motivo = botMessageText.replace(/^Chat encerrado\.?\s*(?:Motivo:\s*)?/i, '').trim();
                  return motivo ? (
                    <div className="px-4 py-2.5 border-b">
                      <p className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground mb-0.5">Motivo</p>
                      <p className="text-sm text-foreground break-words">{motivo}</p>
                    </div>
                  ) : null;
                })()}
                <div className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="h-6 w-6 rounded-full bg-[#002172]/10 flex items-center justify-center shrink-0">
                      <User className="h-3.5 w-3.5 text-[#002172]" />
                    </div>
                    <span className="text-sm text-foreground truncate">{platformUserName ?? attendantName ?? 'Usuário'}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground shrink-0">
                    <Clock className="h-3 w-3" />
                    <span>{formatTime(message.created_at || '')}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : isSystemMessage ? (
            <div className="bg-muted/50 border rounded-full px-4 py-1 text-[10px] text-muted-foreground dark:bg-muted/40 dark:border-border/30" data-system-message>{botMessageText}</div>
          ) : (
            <div className="flex gap-3 max-w-[70%] min-w-0 flex-row-reverse">
              <Avatar className="h-8 w-8 flex-shrink-0" data-message-avatar>
                <AvatarImage
                  src={operatorImgSrc}
                  className="object-cover"
                  onError={() => {
                    if (operatorImgSrc && operatorImgSrc !== defaultOperatorAvatar) setBrokenOperatorAvatarSrc(operatorImgSrc);
                  }}
                />
                <AvatarFallback className={`${isInternalNote ? 'bg-amber-500' : 'bg-[#002172]'} text-white text-[10px]`}>
                  {attendantName?.slice(0, 2).toUpperCase() || 'AT'}
                </AvatarFallback>
              </Avatar>
              <div className={`${isDisparoMessage ? 'bg-violet-50 border border-violet-200 rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px] rounded-tr-[4px]' : isInternalNote ? 'bg-amber-50 border border-amber-200 rounded-[16px]' : 'bg-[#e8eeff] rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px] rounded-tr-[4px]'} px-[17px] pt-[13px] pb-[12px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] min-w-0 overflow-hidden`} data-bot-message={!isInternalNote && !isAttendantMessage} data-internal-note={isInternalNote} data-disparo={isDisparoMessage || undefined}>
                {isDisparoMessage && (
                  <div className="flex items-center gap-1 mb-1.5">
                    <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-violet-600 bg-violet-100 rounded-full px-1.5 py-0.5 uppercase tracking-wide">
                      <Megaphone className="h-2.5 w-2.5" />
                      Disparo
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] font-medium ${isInternalNote ? 'text-amber-700' : isDisparoMessage ? 'text-violet-700' : 'text-[#085e59]'}`}>{attendantName}</span>
                  <span className="text-[10px] text-[#64748b]" data-message-timestamp>{formatTime(message.created_at || "")}</span>
                  {!isInternalNote && (() => {
                    const status = (message as any).delivery_status;
                    const hasMessageId = !!(message as any).message_id;
                    if (status === "failed") {
                      return (
                        <span className="inline-flex items-center text-[10px] text-rose-500" title="Falha no envio">
                          <XOctagon className="h-3 w-3" strokeWidth={2} />
                        </span>
                      );
                    }
                    if (status === "sending") {
                      return (
                        <span className="inline-flex items-center text-[10px] text-slate-400 animate-pulse" title="Enviando...">
                          <Clock className="h-3 w-3" strokeWidth={2} />
                        </span>
                      );
                    }
                    const showOne = status === "sent_to_webhook";
                    const showTwo = status === "delivered" || (!status && hasMessageId);
                    if (!showOne && !showTwo) return null;
                    return (
                      <span className="inline-flex items-center text-[10px] text-blue-400" title={showTwo ? "Confirmado pelo servidor" : "Enviado ao webhook"}>
                        <Check className="h-3 w-3" strokeWidth={2.5} />
                        {showTwo && <Check className="h-3 w-3 -ml-1.5" strokeWidth={2.5} />}
                      </span>
                    );
                  })()}
                </div>
                <div className="space-y-2">
                  {(message.bot_message || (message as any).content || (message as any).content_message) && (
                    <p className="text-sm whitespace-pre-line text-[#020817] break-words overflow-wrap-anywhere">
                      {isInternalNote
                        ? getMessageContent(message.bot_message || (message as any).content || (message as any).content_message || "")
                        : message.bot_message
                          ? getMessageContent(message.bot_message)
                          : ((message as any).content || (message as any).content_message || "")}
                    </p>
                  )}
                  {/* Se não tem texto mas mídia não pode ser renderizada, mostrar indicador */}
                  {!message.bot_message && !(message as any).content && !(message as any).content_message && hasMedia && !canRenderMedia && (
                    <p className="text-sm text-muted-foreground italic">
                      {mediaType?.includes('image') ? '📷 Imagem' :
                       mediaType?.includes('video') ? '🎥 Vídeo' :
                       mediaType?.includes('audio') ? '🎵 Áudio' :
                       '📎 Arquivo'}
                    </p>
                  )}
                  {canRenderMedia && mediaType && (
                    <div className="mt-2">
                      {mediaType.includes('image') && botEffectiveImageSrc && (
                        <div className="relative group max-w-[240px] overflow-hidden rounded-xl shadow-md cursor-pointer" onClick={() => { setMediaInfo({fileType: 'image', fileName: mediaFileName || 'imagem.jpg'}); setShowMediaViewer(true); }}>
                          <img
                            src={botEffectiveImageSrc}
                            className="w-full h-auto max-h-[300px] object-cover"
                            alt={mediaFileName || 'Imagem'}
                            loading="lazy"
                            onError={() => setBotImgBrokenSrcs(prev => new Set([...prev, botEffectiveImageSrc]))}
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />
                        </div>
                      )}
                      {mediaType.includes('image') && !botEffectiveImageSrc && (
                        <p className="text-sm text-muted-foreground italic">📷 Imagem indisponível</p>
                      )}
                      {mediaType.includes('audio') && audioPrimaryUrl && (
                        <div className="max-w-[240px]">
                          <AudioPlayer
                            audioId={`${message.id}-audio-bot`}
                            audioUrl={audioPrimaryUrl}
                            fallbackUrl={audioFallbackUrl && audioFallbackUrl !== audioPrimaryUrl ? audioFallbackUrl : null}
                            fileName={mediaFileName}
                          />
                        </div>
                      )}
                      {mediaType.includes('video') && videoPlayableSrc && (
                        <div className="max-w-[240px] rounded-lg overflow-hidden border border-border/50">
                          <video
                            src={videoPlayableSrc}
                            className="w-full h-auto max-h-[300px]"
                            controls
                            preload="metadata"
                            playsInline
                          />
                        </div>
                      )}
                      {mediaType.includes('document') && (
                        <div className="flex items-center gap-2.5 max-w-[260px] bg-white/60 rounded-xl p-2.5">
                          <div className="h-9 w-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                            <FileText className="h-5 w-5 text-red-500" />
                          </div>
                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => { setMediaInfo({fileType: 'document', fileName: mediaFileName || 'arquivo.pdf'}); setShowMediaViewer(true); }}
                          >
                            <p className="text-[13px] font-medium text-[#111827] truncate leading-tight">{mediaFileName || 'Documento'}</p>
                            <p className="text-[11px] text-[#64748b]">Toque para visualizar</p>
                          </div>
                          <button
                            className="h-8 w-8 rounded-lg bg-white flex items-center justify-center text-[#64748b] hover:text-[#002172] transition-colors flex-shrink-0 shadow-sm border border-gray-100"
                            onClick={handleDownloadFile}
                            title="Baixar arquivo"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {(() => {
                    const msgReactions = (message as any).reactions;
                    if (!Array.isArray(msgReactions) || msgReactions.length === 0) return null;
                    const grouped: Record<string, number> = {};
                    for (const r of msgReactions) {
                      if (r?.emoji) grouped[r.emoji] = (grouped[r.emoji] || 0) + 1;
                    }
                    const entries = Object.entries(grouped);
                    if (entries.length === 0) return null;
                    return (
                      <div className="flex flex-wrap gap-1 pt-1 border-t border-gray-100 mt-1">
                        {entries.map(([emoji, count]) => (
                          <span key={emoji} className="inline-flex items-center gap-0.5 bg-gray-100 rounded-full px-1.5 py-0.5 text-sm leading-none text-gray-700">
                            {emoji}
                            {count > 1 && <span className="text-[10px] font-medium">{count}</span>}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {mediaInfo && (
        <MediaViewer
          open={showMediaViewer}
          onOpenChange={setShowMediaViewer}
          messageId={(message as any).message_id ?? (message as any).id}
          whatsappMessageId={(message as any).id_message_channel ?? null}
          instanceId={instanceId}
          phone={phone}
          fileType={mediaInfo.fileType}
          fileName={mediaInfo.fileName}
          mediaUrl={mediaDataUrl}
        />
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.message.id === nextProps.message.id &&
         prevProps.message.updated_at === nextProps.message.updated_at &&
         prevProps.message.first_viewed_at === nextProps.message.first_viewed_at &&
         JSON.stringify(prevProps.message.read_by) === JSON.stringify(nextProps.message.read_by) &&
         JSON.stringify((prevProps.message as any).reactions) === JSON.stringify((nextProps.message as any).reactions);
});
